import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { DragEndEvent, DragStartEvent } from "@dnd-kit/core"
import { FocusManagementContext, FocusManager } from "./FocusManager"
import { GetCellPropsFn, GetRowPropsFn, TableProps } from "./types"
import { HEADER_HEIGHT, OVERSCAN_COUNT, ROW_HEIGHT } from "./constants"

import { ColumnManager } from "./ColumnManager"
import { Grid } from "./Grid"
import { SortableContainer } from "./Sortable/SortableContainer"
import { SortableOverlay } from "./Sortable/SortableOverlay"
import { TableContext } from "./context"
import { cx } from "@emotion/css"
import { debounce } from "lodash/fp"
import { styles } from "./styles"
import { useCurrent } from "./useCurrent"
import { useRenderCount } from "hooks/useRenderCount" // eslint-disable-line
import { useScroll } from "react-use-gesture"

const empty_object = {}
const _defaultGetCellProps: GetCellPropsFn = () => empty_object
const _defaultGetRowProps: GetRowPropsFn = () => empty_object
const _defaultIsRowSortable = () => false

export function Table(props: TableProps) {
  const {
    columns,
    itemCount,
    getItemKey,
    //
    rowHeight = ROW_HEIGHT,
    headerHeight = HEADER_HEIGHT,
    overscanCount = OVERSCAN_COUNT,
    //
    width = "100%",
    height,
    className,
    onColumnResize: onColumnResizeProp,
    getRowProps = _defaultGetRowProps,
    getCellProps = _defaultGetCellProps,
    overlayClassName,
    overlayText,
    emptyClassName,
    emptyText,
    pending = false,
    rowHoverActions,
    sortable = false,
    onSortStart,
    onSortEnd,
    isRowSortable = _defaultIsRowSortable,
    sortKeys = [],
    draggingRow,
    disabled = false,
  } = props

  const [, getInstance] = useCurrent(() => ({
    getItemKey,
    getRowProps,
    getCellProps,
    onColumnResize: onColumnResizeProp,
    rowHoverActions,
    sortable,
    isRowSortable,
    disabled,
  }))

  Object.assign(getInstance(), {
    getItemKey,
    getRowProps,
    getCellProps,
    onColumnResize: onColumnResizeProp,
    rowHoverActions,
    sortable,
    isRowSortable,
    disabled,
  })

  const [version, setVersion] = useState<number>(() => 0)
  const onUpdate = () => setVersion((v) => v + 1)

  // if we have fixed columns, we need to sync the hover state across the inner grids
  const [hoveredRow, setHoveredRow] = useState<number | null>(() => null)
  const onRowHover = useCallback((index: number, hovered: boolean) => {
    setHoveredRow((_) => (hovered ? index : null))
  }, [])

  //#region Column Management
  const [cm, getColumnManager] = useCurrent(
    () => new ColumnManager({ columns, onUpdate: onUpdate }),
  )
  cm.onReceiveColumns(columns)
  //#endregion

  //#region Focus Management
  const columnsCount = columns.length
  const [fm, getFm] = useCurrent(
    () => new FocusManager(itemCount, columnsCount),
  )
  useEffect(() => {
    getFm().setDimensions(itemCount, columnsCount)
  }, [getFm, itemCount, columnsCount])
  //#endregion

  const containerRef = useRef<HTMLDivElement | null>(null)

  //#region Virtualize
  const scroller = useRef<HTMLDivElement | null>(null)
  const observer = useCallback(() => ({ height, width: 0 }), [height])
  const estimateSize = useCallback(() => rowHeight, [rowHeight])

  const virtualOptions = useMemo(() => {
    return {
      parentRef: scroller,
      size: itemCount,
      overscan: overscanCount,
      keyExtractor: getItemKey,
      useObserver: observer,
      estimateSize: estimateSize,
    }
  }, [scroller, itemCount, getItemKey, overscanCount, observer, estimateSize])
  //#endregion

  const [scrollingFast, setFast] = useState<boolean>(() => false)
  const debouncedFastSetter = useRef(
    debounce(300, (v: boolean) => setFast(v)),
  ).current

  const scroll = useScroll(
    ({ scrolling, velocity, direction: [, vertical] }) => {
      const pointers = scrolling ? "none" : "auto"
      if (scrolling) setHoveredRow(null)
      containerRef.current?.style.setProperty("--pointer-events", pointers)
      const a = vertical ? 0 : 1
      containerRef.current?.style.setProperty("--a", a.toString())
      const limit = vertical !== 0 ? 1 : 9

      if (velocity > limit) {
        debouncedFastSetter.cancel()
        setFast(true)
      } else {
        debouncedFastSetter(false)
      }
    },
  )

  //#region context
  const ctx = useMemo(() => {
    const columnManager = getColumnManager()
    return {
      columnManager,
      ...getInstance(),
      onRowHover: columnManager.hasFixedColumns ? onRowHover : undefined,
      hoveredRow,
      scrollingFast,
    }
    //eslint-disable-next-line
  }, [
    getInstance,
    getColumnManager,
    version,
    onRowHover,
    hoveredRow,
    scrollingFast,
  ])
  //#endregion

  //#region sort
  const [activeId, setActiveId] = useState<string | null>(null)
  const getIndex: (id: string) => number = sortKeys.indexOf.bind(sortKeys)
  function handleDragStart(event: DragStartEvent) {
    if (!sortable || !onSortStart) return
    onSortStart(event.active.id)
    setActiveId(event.active.id)
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!sortable || !onSortEnd) return
    const { active, over } = event
    if (!over) return
    const overIndex = getIndex(over.id)
    const activeIndex = activeId ? getIndex(activeId) : -1
    onSortEnd(active.id, activeIndex, overIndex)
    if (active.id !== over.id) {
      setActiveId(null)
    }
  }
  //#endregion

  //#region render helpers
  const vars = {
    "--viewport-height": `${height}px`,
    "--header-height": `${headerHeight}px`,
    "--row-height": `${rowHeight}px`,
    "--container-left": `${
      containerRef.current?.getBoundingClientRect().left
    }px`,
    "--container-width": `${
      containerRef.current?.getBoundingClientRect().width
    }px`,
    ...cm.layoutVariables,
  } as CSSProperties

  const empty = columnsCount === 0 || itemCount === 0
  const emptyOverlay = (
    <div className={cx(styles.overlay, styles.empty, emptyClassName)}>
      {emptyText || "no data"}
    </div>
  )
  const pendingOverlay = (
    <div className={cx(styles.overlay, styles.pending, overlayClassName)}>
      {overlayText}
    </div>
  )
  //#endregion

  const scrolledLeft = scroller?.current?.scrollLeft

  return (
    <TableContext.Provider value={ctx}>
      <SortableContainer
        keys={sortKeys}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <FocusManagementContext.Provider value={fm}>
          <div
            ref={containerRef}
            className={cx(styles.container, className)}
            style={{ ...vars, width }}
          >
            {pending ? pendingOverlay : null}
            <div
              ref={scroller}
              className={styles.scroller}
              style={{ overflow: "scroll" }}
              {...scroll()}
            >
              {cm.hasFixedColumns ? (
                <Grid area="fixed" virtualOptions={virtualOptions} />
              ) : null}
              <Grid
                area="main"
                virtualOptions={virtualOptions}
                scrolledLeft={scrolledLeft}
              />
              {!pending && empty ? emptyOverlay : null}
            </div>
          </div>
          <SortableOverlay
            activeId={activeId}
            draggingRow={draggingRow}
            cssVariables={vars}
          />
        </FocusManagementContext.Provider>
      </SortableContainer>
    </TableContext.Provider>
  )
}
